import React, { useEffect, useState } from 'react';
import { adminAPI, UserInfo } from '../../api/admin';
import { isPasswordValid, validatePassword } from '../../utils/passwordValidation';
import UserForm, { UserFormData } from '../forms/UserForm';
import type { PasswordValidation } from '../shared';
import { TextInput } from '../shared';
import Button from '../ui/Button';
import Modal from '../ui/Modal';

interface EditUserModalProps {
  isOpen: boolean;
  user: UserInfo | null;
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, user, onClose, onSuccess, onError }) => {
  const [usernameForm, setUsernameForm] = useState({ username: '' });
  const [passwordForm, setPasswordForm] = useState<UserFormData>({
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>(validatePassword(''));

  useEffect(() => {
    if (user) {
      setUsernameForm({ username: user.username });
      setPasswordForm({
        username: '',
        password: '',
        confirmPassword: '',
      });
      setPasswordValidation(validatePassword(''));
    }
  }, [user]);

  const handlePasswordChange = (password: string) => {
    setPasswordForm({ ...passwordForm, password });
    setPasswordValidation(validatePassword(password));
  };

  const handleCancel = () => {
    if (user) {
      setUsernameForm({ username: user.username });
      setPasswordForm({
        username: '',
        password: '',
        confirmPassword: '',
      });
      setPasswordValidation(validatePassword(''));
    }
    onClose();
  };

  const handleUpdateUsername = async () => {
    if (!user || !usernameForm.username.trim()) {
      onError('Username is required');
      return;
    }

    try {
      await adminAPI.updateUsername(user.id, usernameForm.username.trim());
      onSuccess();
    } catch (error: unknown) {
      console.error('Failed to update username:', error);
      onError(error instanceof Error ? error.message : 'Failed to update username');
    }
  };

  const handleUpdatePassword = async () => {
    if (!user || !passwordForm.password) {
      onError('Password is required');
      return;
    }

    if (!isPasswordValid(passwordValidation)) {
      onError('Password does not meet the required criteria');
      return;
    }

    if (passwordForm.password !== passwordForm.confirmPassword) {
      onError('Passwords do not match');
      return;
    }

    try {
      await adminAPI.updatePassword(user.id, passwordForm.password);
      setPasswordForm({
        username: '',
        password: '',
        confirmPassword: '',
      });
      setPasswordValidation(validatePassword(''));
      onSuccess();
    } catch (error) {
      console.error('Failed to update password:', error);
      const message = error instanceof Error ? error.message : 'Failed to update password';
      onError(message);
    }
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title={`Edit User: ${user.username}`} maxWidth="max-w-md">
      <div className="space-y-6">
        {/* Username Update */}
        <div>
          <TextInput
            id="editUsername"
            label="Username"
            value={usernameForm.username}
            onChange={(value) => setUsernameForm({ username: value })}
            placeholder="Enter new username"
          />
          <Button
            variant="primary"
            size="sm"
            onClick={handleUpdateUsername}
            disabled={!usernameForm.username.trim() || usernameForm.username === user.username}
            className="mt-2 w-full"
          >
            Update Username
          </Button>
        </div>

        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-slate-900 mb-3">Change Password</h4>

          <UserForm
            formData={passwordForm}
            passwordValidation={passwordValidation}
            onFormChange={setPasswordForm}
            onPasswordChange={handlePasswordChange}
            showUsername={false}
          />

          <Button
            variant="success"
            size="sm"
            onClick={handleUpdatePassword}
            disabled={
              !passwordForm.password ||
              !passwordForm.confirmPassword ||
              !isPasswordValid(passwordValidation) ||
              passwordForm.password !== passwordForm.confirmPassword
            }
            className="mt-4 w-full"
          >
            Update Password
          </Button>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button variant="secondary" onClick={handleCancel}>
          Done
        </Button>
      </div>
    </Modal>
  );
};

export default EditUserModal;
